import { useCallback, useEffect, useId, useMemo, useReducer, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

import {
  BOOKING_PROVIDERS,
  DEFAULT_TAP_DOCUMENT,
  SERVICE_PROVIDERS,
  SOCIAL_PROVIDERS,
  TAP_ACTION_LIMIT,
  createAction,
  getActionLabel,
  getBookingLabel,
  getServiceLabel,
  getSocialLabel,
  renderTapDocument,
  sanitizeDocument,
  validateTapDocument
} from '../../lib/tapDocument';
import type {
  TapAction,
  TapActionType,
  TapBookingProvider,
  TapDocument,
  TapProjectData,
  TapServiceProvider,
  TapSocialProvider,
  TapTheme
} from '../../lib/tapDocument';
import { API_BASE_URL } from '../../lib/api';
import {
  fetchProviderRegistry,
  fetchFrameabilityStatus,
  matchesProviderUrl,
  type ProviderRegistryEntry
} from '../../lib/providers';
import { initializeBookingOverlay, type FrameabilityResult } from '../../lib/bookingOverlay';
import { useSession } from '../../session';
import { UploadDrawer, type UploadRecord } from '../../components/builder/UploadDrawer';
import { TapActionIcon } from './TapActionIcon';
import { useTapDraftSafety, type DraftState } from './useTapDraftSafety';
import './tap-builder-refresh.css';

type PageMeta = {
  title: string;
  description: string;
  slug: string;
};

type TapBuilderProps = {
  initialSlug?: string;
  initialPublicSlug?: string | null;
  apiBaseUrl?: string;
  onBackToTapPage?: () => void;
};

type BuilderDevice = 'mobile' | 'tablet' | 'desktop';
type EditorSection = 'content' | 'appearance' | 'preview' | 'publish';

type UploadTarget =
  | { kind: 'profile-photo' }
  | { kind: 'action'; actionId: string; actionType: TapActionType };


const ACTION_OPTIONS: { value: TapActionType; label: string }[] = [
  { value: 'download-card', label: 'Download card' },
  { value: 'booking', label: 'Booking' },
  { value: 'service-book', label: 'Booking (Services)' },
  { value: 'website', label: 'Website' },
  { value: 'social', label: 'Social profile' },
  { value: 'file', label: 'File download' },
  { value: 'custom', label: 'Custom link' }
];

const LABEL_EDITABLE_TYPES: TapActionType[] = [
  'download-card',
  'booking',
  'service-book',
  'website',
  'social',
  'file',
  'custom'
];

const VIEWPORT_WIDTH: Record<BuilderDevice, number> = { mobile: 375, tablet: 768, desktop: 1280 };

const DEVICE_WIDTH: Record<BuilderDevice, number> = {
  desktop: 420,
  tablet: 360,
  mobile: 320
};

const createInitialMeta = (slug: string | undefined): PageMeta => ({
  title: 'Tap landing page',
  description: 'Tap to connect with me.',
  slug: (slug ?? 'your-name').toLowerCase()
});

const BOOKING_TYPE_PRESETS: Array<{
  value: Extract<TapActionType, 'booking' | 'service-book'>;
  label: string;
  description: string;
}> = [
  {
    value: 'booking',
    label: 'Meetings',
    description: 'Calendly, HubSpot, SavvyCal, Chili Piper, Google, Outlook'
  },
  {
    value: 'service-book',
    label: 'Services',
    description: 'Acuity, Square, SimplyBook.me, Booksy, Mindbody, Setmore'
  }
];

const buildProjectData = (tapDoc: TapDocument): TapProjectData => ({
  version: 'tap-app/v3',
  document: tapDoc
});

const composeForceEmbedKey = (providerId: string, url: string) => `${providerId}::${url}`;

type EditorSnapshot = { meta: PageMeta; document: TapDocument };
type EditorHistory = { current: EditorSnapshot; past: EditorSnapshot[]; future: EditorSnapshot[]; lastGroup?: number };
type HistoryAction =
  | { type: 'hydrate-meta'; value: React.SetStateAction<PageMeta> }
  | { type: 'hydrate-document'; value: React.SetStateAction<TapDocument> }
  | { type: 'edit-meta'; value: (current: PageMeta) => PageMeta; group?: number }
  | { type: 'edit-document'; value: (current: TapDocument) => TapDocument; group?: number }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'clear-history' };

const HISTORY_LIMIT = 100;
function editorHistoryReducer(state: EditorHistory, action: HistoryAction): EditorHistory {
  if (action.type === 'clear-history') return { ...state, past: [], future: [], lastGroup: undefined };
  if (action.type === 'undo') {
    if (!state.past.length) return state;
    return { current: state.past[state.past.length - 1], past: state.past.slice(0, -1),
      future: [state.current, ...state.future].slice(0, HISTORY_LIMIT) };
  }
  if (action.type === 'redo') {
    if (!state.future.length) return state;
    return { current: state.future[0], past: [...state.past, state.current].slice(-HISTORY_LIMIT),
      future: state.future.slice(1) };
  }
  const { current } = state;
  if (action.type === 'hydrate-meta') {
    const meta = typeof action.value === 'function' ? action.value(current.meta) : action.value;
    return { current: { ...current, meta }, past: [], future: [] };
  }
  if (action.type === 'hydrate-document') {
    const document = typeof action.value === 'function' ? action.value(current.document) : action.value;
    return { current: { ...current, document }, past: [], future: [] };
  }
  const next: EditorSnapshot = action.type === 'edit-meta'
    ? { ...current, meta: action.value(current.meta) }
    : { ...current, document: action.value(current.document) };
  if (next.meta === current.meta && next.document === current.document) return state;
  // A slug changes the target document. Never restore edits from another target.
  if (next.meta.slug !== current.meta.slug) return { current: next, past: [], future: [] };
  // Consecutive changes in one focused text field are one undoable edit.
  const grouped = action.group !== undefined && action.group === state.lastGroup && state.future.length === 0;
  return { current: next, past: grouped ? state.past : [...state.past, current].slice(-HISTORY_LIMIT),
    future: [], lastGroup: action.group };
}

const TapBuilderEditor = ({
  initialSlug,
  initialPublicSlug,
  apiBaseUrl,
  onBackToTapPage
}: TapBuilderProps) => {
  const toolsFocusAfterResize = useRef<string | null>(null);
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [compactTools, setCompactTools] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 600px)').matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(max-width: 600px)');
    const update = () => {
      const focused = document.activeElement as HTMLElement | null;
      if (focused && actionsRef.current?.contains(focused) &&
          (focused.matches('[data-secondary-tool]') || focused.closest('.tap-builder__mobile-tools'))) {
        toolsFocusAfterResize.current = media.matches ? 'summary' : (focused.dataset.secondaryTool ?? 'fallback');
      }
      setCompactTools(media.matches);
    };
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const target = toolsFocusAfterResize.current;
    if (!target) return;
    toolsFocusAfterResize.current = null;
    const root = actionsRef.current;
    const next = target === 'summary'
      ? root?.querySelector<HTMLElement>('.tap-builder__mobile-tools summary')
      : root?.querySelector<HTMLElement>(`[data-secondary-tool="${target}"]:not(:disabled)`);
    (next ?? root?.querySelector<HTMLElement>('[data-toolbar-save]:not(:disabled)') ?? root?.parentElement?.querySelector<HTMLElement>('.tap-builder__brand [tabindex]'))?.focus();
  }, [compactTools]);
  const sessionContext = useSession();
  const session = sessionContext.session;
  const canSyncToCloud = session?.canSyncToCloud ?? false;
  const actionLimit = session?.limits?.actionLimit === undefined ? TAP_ACTION_LIMIT : session.limits.actionLimit;
  const endpoint = apiBaseUrl ?? API_BASE_URL;

  const [editor, dispatchEditor] = useReducer(editorHistoryReducer, initialSlug, (slug): EditorHistory => ({
    current: { meta: createInitialMeta(slug), document: DEFAULT_TAP_DOCUMENT }, past: [], future: []
  }));
  const { meta, document: tapDocument } = editor.current;
  const textEditRef = useRef<{ target: HTMLInputElement | HTMLTextAreaElement; group: number } | null>(null);
  const nextTextEditGroupRef = useRef(0);
  const endTextEdit = useCallback(() => { textEditRef.current = null; }, []);
  const textEditGroup = useCallback((target: HTMLInputElement | HTMLTextAreaElement) => {
    if (textEditRef.current?.target !== target) {
      textEditRef.current = { target, group: ++nextTextEditGroupRef.current };
    }
    return textEditRef.current.group;
  }, []);
  const setMeta = useCallback((value: React.SetStateAction<PageMeta>) => {
    endTextEdit();
    dispatchEditor({ type: 'hydrate-meta', value });
  }, [endTextEdit]);
  const setTapDocument = useCallback((value: React.SetStateAction<TapDocument>) => {
    endTextEdit();
    dispatchEditor({ type: 'hydrate-document', value });
  }, [endTextEdit]);
  const [publicSlug, setPublicSlug] = useState<string | null>(initialPublicSlug ?? null);
  const [activeDevice, setActiveDevice] = useState<BuilderDevice>('mobile');
  const [showViewportPreview, setShowViewportPreview] = useState(false);
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const [previewPhotoKey, setPreviewPhotoKey] = useState(0);
  const [loadedPhotoUrl, setLoadedPhotoUrl] = useState<string | null>(null);
  const [photoUrlEditing, setPhotoUrlEditing] = useState(false);
  const photoUrlRef = useRef<HTMLInputElement | null>(null);
  const photoUrl = tapDocument.profile.photoUrl ?? '';
  const photoFailed = Boolean(photoUrl && failedPhotoUrl === photoUrl);
  const showPhotoError = photoFailed && !photoUrlEditing;
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => { setFailedPhotoUrl(null); }, [photoUrl]);

  const [isUploadDrawerOpen, setUploadDrawerOpen] = useState<boolean>(false);
  const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);

  const [nextActionType, setNextActionType] = useState<TapActionType>('website');
  const [expandedActions, setExpandedActions] = useState<Set<string>>(() => new Set());
  const [actionAnnouncement, setActionAnnouncement] = useState('');
  const actionToggleRefs = useRef(new Map<string, HTMLButtonElement>());
  const actionsHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const pendingActionFocus = useRef<{ id: string | null } | null>(null);

  useEffect(() => {
    const pending = pendingActionFocus.current;
    if (!pending) return;
    pendingActionFocus.current = null;
    const target = pending.id ? actionToggleRefs.current.get(pending.id) : null;
    (target ?? actionsHeadingRef.current)?.focus();
  }, [tapDocument.actions]);

  const [providerRegistry, setProviderRegistry] = useState<ProviderRegistryEntry[]>([]);
  const [providerRegistryStatus, setProviderRegistryStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [forceEmbedOverrides, setForceEmbedOverrides] = useState<Record<string, boolean>>({});

  const providerMap = useMemo(() => {
    const map = new Map<string, ProviderRegistryEntry>();
    providerRegistry.forEach((entry) => map.set(entry.id, entry));
    return map;
  }, [providerRegistry]);
  const forceEmbedKey = useMemo(
    () =>
      Object.keys(forceEmbedOverrides)
        .filter((key) => forceEmbedOverrides[key])
        .sort()
        .join('|'),
    [forceEmbedOverrides]
  );

  const frameabilityCacheRef = useRef<Map<string, Promise<FrameabilityResult>>>(new Map());
  const getFrameability = useCallback(
    (providerId: string, url: string) => {
      const cacheKey = `${providerId}::${url}`;
      let promise = frameabilityCacheRef.current.get(cacheKey);
      if (!promise) {
        promise = fetchFrameabilityStatus(providerId, url).then((result) => ({
          status: result.status,
          reason: result.reason ?? null
        }));
        promise.catch(() => {
          frameabilityCacheRef.current.delete(cacheKey);
        });
        frameabilityCacheRef.current.set(cacheKey, promise);
      }
      return promise;
    },
    []
  );

  const isForceEmbedEnabled = useCallback(
    (providerId?: string | null, rawUrl?: string | null) => {
      if (!providerId || !rawUrl) {
        return false;
      }
      const trimmed = rawUrl.trim();
      if (!trimmed) {
        return false;
      }
      return Boolean(forceEmbedOverrides[composeForceEmbedKey(providerId, trimmed)]);
    },
    [forceEmbedOverrides]
  );

  const handleForceEmbedChange = useCallback((providerId: string, rawUrl: string, enabled: boolean) => {
    const trimmed = rawUrl.trim();
    if (!providerId || !trimmed) {
      return;
    }
    setForceEmbedOverrides((previous) => {
      const next = { ...previous };
      const key = composeForceEmbedKey(providerId, trimmed);
      if (enabled) {
        next[key] = true;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  const bookingProviderOptions = useMemo(() => {
    const meetingProviders = providerRegistry.filter((entry) => entry.category === 'meeting');
    if (meetingProviders.length === 0) {
      return BOOKING_PROVIDERS;
    }
    return meetingProviders
      .map((entry) => ({
        value: entry.id as TapBookingProvider,
        label: entry.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providerRegistry]);

  const serviceProviderOptions = useMemo(() => {
    const services = providerRegistry.filter((entry) => entry.category === 'service');
    if (services.length === 0) {
      return SERVICE_PROVIDERS;
    }
    return services
      .map((entry) => ({
        value: entry.id as TapServiceProvider,
        label: entry.name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providerRegistry]);

  const editRevisionRef = useRef(0);
  const currentSlug = meta.slug;
  const hydrateDraft = useCallback((draft: DraftState) => {
    setMeta(draft.meta);
    setTapDocument(draft.projectData.document);
    dispatchEditor({ type: 'clear-history' });
  }, [setMeta, setTapDocument]);
  const safety = useTapDraftSafety({
    endpoint, slug: currentSlug, session, canSync: canSyncToCloud,
    state: { meta, projectData: buildProjectData(tapDocument), savedAt: new Date().toISOString() },
    editRevision: editRevisionRef, hydrate: hydrateDraft, setPublicSlug
  });
  const { status, setStatus, busy: isSaving } = safety;

  useEffect(() => {
    let cancelled = false;
    fetchProviderRegistry()
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setProviderRegistry(payload.providers);
        setProviderRegistryStatus('ready');
      })
      .catch((error) => {
        console.warn('Failed to load provider registry', error);
        if (cancelled) {
          return;
        }
        setProviderRegistryStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleMetaChange = useCallback(<T extends keyof PageMeta>(key: T, value: PageMeta[T], group?: number) => {
    editRevisionRef.current += 1;
    dispatchEditor({ type: 'edit-meta', group, value: (current) => ({
      ...current,
      [key]: key === 'slug' ? String(value).toLowerCase() : value
    }) });
  }, []);

  const updateTapDocument = useCallback((mutator: (previous: TapDocument) => TapDocument, group?: number) => {
    if (group === undefined) endTextEdit();
    editRevisionRef.current += 1;
    dispatchEditor({ type: 'edit-document', value: mutator, group });
  }, [endTextEdit]);

  const restoreHistory = useCallback((direction: 'undo' | 'redo') => {
    if (isUploadDrawerOpen || (direction === 'undo' && editor.past.length === 0) || (direction === 'redo' && editor.future.length === 0)) return;
    endTextEdit();
    editRevisionRef.current += 1;
    dispatchEditor({ type: direction });
    setValidationErrors([]);
  }, [editor.past.length, editor.future.length, isUploadDrawerOpen, endTextEdit]);

  // Leave native undo and input-method composition to editable controls. These
  // shortcuts operate only while focus is inside the builder, outside its drawer.
  const handleHistoryShortcut = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.nativeEvent.isComposing || event.altKey || isUploadDrawerOpen ||
        event.ctrlKey === event.metaKey || !(event.target instanceof Element)) return;
    const target = event.target;
    if (target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="dialog"], [role="menu"], [role="listbox"], [role="combobox"], dialog')) return;
    const key = event.key.toLowerCase();
    const direction = key === 'z' ? (event.shiftKey ? 'redo' : 'undo')
      : key === 'y' && event.ctrlKey && !event.shiftKey ? 'redo' : null;
    if (!direction || (direction === 'undo' && !editor.past.length) || (direction === 'redo' && !editor.future.length)) return;
    event.preventDefault();
    restoreHistory(direction);
  }, [editor.past.length, editor.future.length, isUploadDrawerOpen, restoreHistory]);
  const historyShortcutHelp = useId();

  const handleThemeChange = useCallback((theme: TapTheme) => {
    updateTapDocument((previous) => ({
      ...previous,
      theme
    }));
  }, [updateTapDocument]);

  const handleProfileChange = useCallback((field: keyof TapDocument['profile'], value: string, group?: number) => {
    updateTapDocument((previous) => ({
      ...previous,
      profile: {
        ...previous.profile,
        [field]: value
      }
    }), group);
  }, [updateTapDocument]);

  const handleAddAction = useCallback(() => {
    const action = createAction(nextActionType);
    setExpandedActions((previous) => new Set(previous).add(action.id));
    updateTapDocument((previous) => {
      if (actionLimit !== null && previous.actions.length >= actionLimit) {
        return previous;
      }
      return {
        ...previous,
        actions: [...previous.actions, action]
      };
    });
  }, [actionLimit, nextActionType, updateTapDocument]);

  const handleActionChange = useCallback((id: string, updates: Partial<TapAction>, group?: number) => {
    updateTapDocument((previous) => ({
      ...previous,
      actions: previous.actions.map((action) => {
        if (action.id !== id) {
          return action;
        }

        const next: TapAction = {
          ...action,
          ...updates
        };

        if (Object.prototype.hasOwnProperty.call(updates, 'provider')) {
          const currentLabel = (action.label ?? '').trim();

          if (action.type === 'booking') {
            const previousDefault = getBookingLabel(action.provider as TapBookingProvider | undefined);
            const nextDefault = getBookingLabel((updates.provider as TapBookingProvider | undefined) ?? 'calendly');
            if (!currentLabel || currentLabel === previousDefault) {
              next.label = nextDefault;
            }
          } else if (action.type === 'service-book') {
            const previousDefault = getServiceLabel(action.provider as TapServiceProvider | undefined);
            const nextDefault = getServiceLabel((updates.provider as TapServiceProvider | undefined) ?? 'acuity');
            if (!currentLabel || currentLabel === previousDefault) {
              next.label = nextDefault;
            }
          } else if (action.type === 'social') {
            const previousDefault = getSocialLabel(action.provider as TapSocialProvider | undefined);
            const nextDefault = getSocialLabel((updates.provider as TapSocialProvider | undefined) ?? 'linkedin');
            if (!currentLabel || currentLabel === previousDefault) {
              next.label = nextDefault;
            }
          }
        }

        if (Object.prototype.hasOwnProperty.call(updates, 'label')) {
          const nextLabelUpdate = updates.label;
          if (typeof nextLabelUpdate === 'string') {
            next.label = nextLabelUpdate;
          } else if (nextLabelUpdate === undefined || nextLabelUpdate === null) {
            next.label = undefined;
          }
        }

        return next;
      })
    }), group);
  }, [updateTapDocument]);

  const handleActionTypeChange = useCallback((id: string, type: TapActionType) => {
    const replacement = createAction(type);
    updateTapDocument((previous) => ({
      ...previous,
      actions: previous.actions.map((action) => {
        if (action.id !== id) {
          return action;
        }
        return {
          ...replacement,
          id: action.id,
          label: action.label && action.label.trim().length > 0 ? action.label : replacement.label,
          url: action.url ?? replacement.url
        };
      })
    }));
  }, [updateTapDocument]);

  const handleRemoveAction = useCallback((id: string) => {
    updateTapDocument((previous) => ({
      ...previous,
      actions: previous.actions.filter((action) => action.id !== id)
    }));
  }, [updateTapDocument]);

  const handleMoveAction = useCallback((id: string, direction: -1 | 1) => {
    updateTapDocument((previous) => {
      const index = previous.actions.findIndex((action) => action.id === id);
      if (index === -1) {
        return previous;
      }
      const target = previous.actions[index + direction];
      if (!target) {
        return previous;
      }
      const nextActions = [...previous.actions];
      nextActions[index] = target;
      nextActions[index + direction] = previous.actions[index];
      return {
        ...previous,
        actions: nextActions
      };
    });
  }, [updateTapDocument]);

  const openUploadDrawer = useCallback((target: UploadTarget) => {
    if (!canSyncToCloud) {
      setStatus('Uploads unlock after you order a card or upgrade.');
      return;
    }
    setUploadTarget(target);
    setUploadDrawerOpen(true);
  }, [canSyncToCloud]);

  const handleCloseUploadDrawer = useCallback(() => {
    setUploadDrawerOpen(false);
    setUploadTarget(null);
  }, []);

  const handleInsertUpload = useCallback((record: UploadRecord) => {
    if (!uploadTarget) {
      return;
    }
    if (uploadTarget.kind === 'profile-photo') {
      handleProfileChange('photoUrl', record.url);
      handleCloseUploadDrawer();
      return;
    }

    if (uploadTarget.kind === 'action') {
      handleActionChange(uploadTarget.actionId, {
        url: record.url,
        label:
          uploadTarget.actionType === 'download-card'
            ? 'Download contact'
            : uploadTarget.actionType === 'file' && (!record.name || record.name.trim().length === 0)
            ? 'Download file'
            : uploadTarget.actionType === 'file'
            ? record.name ?? undefined
            : record.name ?? undefined
      });
      handleCloseUploadDrawer();
      return;
    }
  }, [uploadTarget, handleProfileChange, handleActionChange, handleCloseUploadDrawer]);

  // The local preview and backup show the complete retained draft. Plan limits
  // still govern validation and server-side publication.
  const { html: previewHtml, css: previewCss } = useMemo(
    () => renderTapDocument(tapDocument, { actionLimit: null }),
    [tapDocument]
  );
  const previewMarkup = useMemo(() => {
    const marker = forceEmbedKey
      ? `<script type="application/json" data-force-embed-map>${JSON.stringify(forceEmbedOverrides)}</script>`
      : '';
    return `<style>${previewCss}</style>${previewHtml}${marker}`;
  }, [previewCss, previewHtml, forceEmbedOverrides]);

  // A CSS image has a transparent failure state, preserving the renderer's
  // initials if the sandbox cannot load a photo that the editor verified.
  const viewportPhotoCss = useMemo(() => {
    if (!photoUrl || photoFailed || loadedPhotoUrl !== photoUrl) return '';
    const escapedUrl = Array.from(photoUrl.trim(), (character) =>
      '\\' + character.codePointAt(0)!.toString(16).padStart(6, '0')).join('');
    return `.tap-profile__photo--image::before { content: ""; position: absolute; inset: 0; z-index: 1; background: center / cover no-repeat image-set(url("${escapedUrl}") 1x); }`;
  }, [photoUrl, photoFailed, loadedPhotoUrl]);

  const [editorStage, setEditorStage] = useState<EditorSection>('content');
  const detailsHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const previewHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const appearanceRef = useRef<HTMLSpanElement | null>(null);
  const publishHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const validationHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const publishButtonRef = useRef<HTMLButtonElement | null>(null);
  const previewContentRef = useRef<HTMLDivElement | null>(null);
  const pendingStageFocus = useRef<EditorSection | 'validation' | null>(null);
  const goToStage = useCallback((stage: EditorSection, focusValidation = false) => {
    // An interrupted preview dialog must not keep focus trapped over another stage.
    previewContentRef.current?.querySelector<HTMLButtonElement>('.tap-booking-modal:not([hidden]) .tap-booking-modal__close')?.click();
    const targetName = focusValidation ? 'validation' : stage;
    pendingStageFocus.current = targetName;
    if (stage === editorStage) {
      const target = targetName === 'content' ? detailsHeadingRef.current : targetName === 'appearance' ? appearanceRef.current : targetName === 'preview' ? previewHeadingRef.current : targetName === 'validation' ? validationHeadingRef.current : publishHeadingRef.current;
      target?.focus({ preventScroll: true });
      target?.scrollIntoView?.({ block: 'start' });
      pendingStageFocus.current = null;
    } else {
      setEditorStage(stage);
    }
  }, [editorStage]);
  useEffect(() => {
    const targetName = pendingStageFocus.current;
    if (targetName !== editorStage && !(targetName === 'validation' && editorStage === 'publish')) return;
    const target = targetName === 'content' ? detailsHeadingRef.current : targetName === 'appearance' ? appearanceRef.current : targetName === 'preview' ? previewHeadingRef.current : targetName === 'validation' ? validationHeadingRef.current : publishHeadingRef.current;
    target?.focus({ preventScroll: true });
    target?.scrollIntoView?.({ block: 'start' });
    pendingStageFocus.current = null;
  }, [editorStage]);

  const overlayCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const container = previewContentRef.current;
    overlayCleanupRef.current?.();

    if (!container) {
      overlayCleanupRef.current = null;
      return;
    }

    // A previously failed CSS image can remain cached in this document after
    // retry. A verified image overlays the same shared-renderer fallback here.
    const photo = container.querySelector('.tap-profile__photo--image');
    let verifiedPhoto: HTMLImageElement | null = null;
    if (photo && photoUrl && loadedPhotoUrl === photoUrl) {
      verifiedPhoto = document.createElement('img');
      verifiedPhoto.className = 'tap-preview__verified-photo';
      verifiedPhoto.alt = '';
      verifiedPhoto.addEventListener('error', () => verifiedPhoto?.remove(), { once: true });
      verifiedPhoto.src = photoUrl;
      photo.appendChild(verifiedPhoto);
    }

    overlayCleanupRef.current = initializeBookingOverlay({
      root: container,
      getProvider: (providerId) => providerMap.get(providerId),
      fetchFrameability: getFrameability,
      shouldForceEmbed: ({ providerId, url }) => isForceEmbedEnabled(providerId, url ?? null)
    });

    return () => {
      verifiedPhoto?.remove();
      overlayCleanupRef.current?.();
      overlayCleanupRef.current = null;
    };
  }, [previewHtml, providerMap, getFrameability, isForceEmbedEnabled, forceEmbedKey, previewPhotoKey, photoUrl, loadedPhotoUrl]);

  const handleDeviceChange = useCallback((device: BuilderDevice) => {
    setActiveDevice(device);
  }, []);

  const canUseUploads = Boolean(currentSlug) && canSyncToCloud && safety.ready && !safety.review;

  const performValidation = useCallback(() => {
    const errors = validateTapDocument(tapDocument, { actionLimit });
    setValidationErrors(errors);
    if (errors.length > 0) setExpandedActions(new Set(tapDocument.actions.map((action) => action.id)));
    return errors.length === 0;
  }, [actionLimit, tapDocument]);

  const handleSave = useCallback(async () => {
    endTextEdit();
    if (!performValidation()) {
      setStatus('Fix validation errors before saving.');
      goToStage('publish', true);
      return;
    }
    await safety.save();
  }, [endTextEdit, performValidation, setStatus, goToStage, safety.save]);
  const handlePublish = useCallback(async () => {
    endTextEdit();
    if (!performValidation()) {
      setStatus('Fix validation errors before publishing.');
      goToStage('publish', true);
      return;
    }
    await safety.publish();
  }, [endTextEdit, performValidation, setStatus, goToStage, safety.publish]);

  const handlePreview = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const previewWindow = window.open('', 'tap-preview');
    if (!previewWindow) {
      setStatus('Allow pop-ups to open the preview.');
      return;
    }
    previewWindow.document.write(`
<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${meta.title ?? 'Tap preview'}</title>
    <style>${previewCss}</style>
  </head>
  <body>${previewHtml}</body>
</html>
    `);
    previewWindow.document.close();
    setStatus('Opened preview in a new tab.');
  }, [meta.title, previewCss, previewHtml]);

  const handleDownloadBackup = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const blob = new Blob(
      [
        '<!doctype html>',
        '<html><head><meta charset="utf-8" />',
        `<title>${meta.title ?? 'tap-app-backup'}</title>`,
        `<style>${previewCss}</style>`,
        '</head><body>',
        previewHtml,
        '</body></html>'
      ],
      { type: 'text/html' }
    );
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${meta.slug || 'tap-app'}.html`;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setStatus('Downloaded HTML backup.');
  }, [meta.slug, meta.title, previewCss, previewHtml]);

  const handleClearPhoto = useCallback(() => {
    handleProfileChange('photoUrl', '');
  }, [handleProfileChange]);

  const hasAvailableActions = actionLimit === null || tapDocument.actions.length < actionLimit;

  const undoControl = <button data-secondary-tool="undo" type="button" onClick={() => restoreHistory('undo')} disabled={isUploadDrawerOpen || !editor.past.length}
    aria-describedby={historyShortcutHelp} aria-keyshortcuts="Control+Z Meta+Z" title="Undo: Ctrl or Command + Z outside text fields">Undo</button>;
  const redoControl = <button data-secondary-tool="redo" type="button" onClick={() => restoreHistory('redo')} disabled={isUploadDrawerOpen || !editor.future.length}
    aria-describedby={historyShortcutHelp} aria-keyshortcuts="Control+Shift+Z Meta+Shift+Z Control+Y" title="Redo: Ctrl or Command + Shift + Z outside text fields">Redo</button>;
  const previewControl = <button data-secondary-tool="preview" type="button" onClick={handlePreview}>Preview</button>;
  const downloadControl = <button data-secondary-tool="download" type="button" onClick={handleDownloadBackup}>Download HTML</button>;
  const deviceSaveFailed = Boolean(safety.deviceIssue);
  const deviceWarningInStatus = /unable to save on this device|draft could not be saved on this device/i.test(status);

  return (
    <div className="tap-builder" data-stage={editorStage} onKeyDown={handleHistoryShortcut} onBlurCapture={endTextEdit}>
      <header className="tap-builder__topbar">
        {onBackToTapPage ? (
          <div className="tap-builder-screen__back">
            <button type="button" onClick={onBackToTapPage}>Back to Tap Page</button>
          </div>
        ) : null}
        <div className="tap-builder__brand">
          <span>InkTap Tap Builder</span>
          <span className={deviceWarningInStatus ? 'tap-builder__status tap-builder__status--warning' : 'tap-builder__status'}
            role={deviceWarningInStatus ? 'alert' : 'status'}>{status}</span>
          {deviceSaveFailed && !deviceWarningInStatus ? (
            <span className="tap-builder__status tap-builder__status--warning" role="alert">{safety.deviceIssue}</span>
          ) : null}
        </div>
        <div className="tap-builder__actions" ref={actionsRef}>
          <span id={historyShortcutHelp} className="sr-only">When focus is outside a text field, use Control or Command plus Z to undo, and Control or Command plus Shift plus Z to redo. Control plus Y also redoes.</span>
          {!compactTools && <>{undoControl}{redoControl}</>}
          <button type="button" className="tap-builder__jump" onClick={() => goToStage('preview')}>
            Go to live preview
          </button>
          {!compactTools && previewControl}
          <button type="button" data-toolbar-save onClick={handleSave} disabled={isSaving || !safety.ready || Boolean(safety.review)}>
            Save draft
          </button>
          <button
            className="primary"
            type="button"
            ref={publishButtonRef}
            data-editor-section="publish"
            onClick={handlePublish}
            disabled={isSaving || !canSyncToCloud || !safety.ready || Boolean(safety.review)}
            title={canSyncToCloud ? undefined : 'Order a card or upgrade to publish your landing page.'}
          >
            Publish live page
          </button>
          {!compactTools && downloadControl}
          {compactTools && (
            <details className="tap-builder__mobile-tools" onKeyDown={(event) => {
              if (event.key === 'Escape' && event.currentTarget.open) {
                event.preventDefault();
                event.stopPropagation();
                event.currentTarget.open = false;
                event.currentTarget.querySelector('summary')?.focus();
              }
            }}>
              <summary>More tools</summary>
              <div className="tap-builder__mobile-tools-list">
                {undoControl}{redoControl}{previewControl}{downloadControl}
              </div>
            </details>
          )}
        </div>
        <nav className="tap-builder__sections" aria-label="Tap Page editor sections">
          <button type="button" aria-current={editorStage === 'content' ? 'location' : undefined} onClick={() => goToStage('content')}><span aria-hidden="true">1</span>Content</button>
          <button type="button" aria-current={editorStage === 'appearance' ? 'location' : undefined} onClick={() => goToStage('appearance')}><span aria-hidden="true">2</span>Appearance</button>
          <button type="button" aria-current={editorStage === 'preview' ? 'location' : undefined} onClick={() => goToStage('preview')}><span aria-hidden="true">3</span>Preview</button>
          <button type="button" aria-label="Go to publish controls" aria-current={editorStage === 'publish' ? 'location' : undefined} onClick={() => goToStage('publish')}><span aria-hidden="true">4</span>Publish</button>
        </nav>
      </header>
      <section className="tap-draft-safety" aria-label="Draft safety and recovery">
        {safety.review ? (
          <div className="tap-draft-safety__review" role="region" aria-label="Review retained drafts">
            <h2>Choose the draft to continue</h2>
            <p>Your current editor and stored copy have been preserved. Choosing the cloud draft replaces the editor and, when device saving is available, the owned device copy.</p>
            <p>Cloud: {safety.review.cloud.meta.title}. {safety.review.device ? "Owned device draft saved " + new Date(safety.review.device.state.savedAt).toLocaleString() + "." : "No recoverable owned device draft."}</p>
            <div className="tap-draft-safety__choices">
              <button type="button" onClick={() => { safety.choose('current'); goToStage('content'); }}>Keep current editor</button>
              {safety.review.device ? <button type="button" onClick={() => { safety.choose('device'); goToStage('content'); }}>Use owned device draft</button> : null}
              <button type="button" onClick={() => { safety.choose('cloud'); goToStage('content'); }}>Use cloud draft</button>
            </div>
          </div>
        ) : null}
        {safety.lastConfirmed ? <p role="status">{safety.lastConfirmed}</p> : null}
        {safety.attempt ? <p role="status">{safety.attempt}</p> : null}
        <details>
          <summary>Device storage and recovery</summary>
          <p>This browser keeps one ownership-bound draft until you replace or explicitly delete it. Other browser users may access stored data. Clearing browser storage removes the copy. Edits held only in memory can be lost when you leave the editor, sign out, or switch workspaces. HTML download is a preview export, not a restorable editor backup.</p>
          <button type="button" onClick={safety.refresh} disabled={isSaving}>Refresh and review cloud draft</button>
          {safety.legacyPresent ? <p>A previous device draft has no verified ownership. Its bytes are preserved and its contents are not displayed or restored.</p> : null}
          <button type="button" onClick={() => safety.setDeleteTarget('owned')}>Delete stored device copy</button>
          {safety.legacyPresent ? <button type="button" onClick={() => safety.setDeleteTarget('legacy')}>Delete previous unverified copy</button> : null}
          {safety.deleteTarget ? <div role="group" aria-label="Confirm device copy deletion">
            <p>Delete this {safety.deleteTarget === 'legacy' ? 'previous unverified' : 'stored'} copy from this browser? This cannot be undone. Your current editor remains in memory.</p>
            <button type="button" onClick={safety.removeCopy}>Confirm delete copy</button>
            <button type="button" onClick={() => safety.setDeleteTarget(null)}>Cancel deletion</button>
          </div> : null}
          {safety.paused ? <div><p>Device saving is paused after deletion. Editing and cloud saving do not recreate the copy.</p><button type="button" onClick={safety.resume} disabled={!safety.ready || Boolean(safety.review)}>Resume device saving</button></div> : null}
        </details>
      </section>
      <div className="tap-builder__body">
        <aside className="tap-builder__form" aria-label="Tap Page content" hidden={editorStage === 'preview' || editorStage === 'publish'}>
          <section className="tap-panel" data-editor-section="content" hidden={editorStage !== 'content'}>
            <h2 ref={detailsHeadingRef} tabIndex={-1}>Your details</h2>
            <p className="tap-helper-text">This information appears at the top of your Tap Page.</p>
            <div className="tap-profile-identity">
              <div className="tap-profile-photo-controls">
                <div className="tap-profile-photo" aria-hidden="true">
                  <span>{tapDocument.profile.name.trim().charAt(0).toUpperCase() || '?'}</span>
                  {photoUrl && !photoFailed ? (
                    <img key={photoUrl} src={photoUrl} alt="" onLoad={() => { setLoadedPhotoUrl(photoUrl); setPreviewPhotoKey((key) => key + 1); }} onError={() => { setLoadedPhotoUrl(null); setFailedPhotoUrl(photoUrl); }} />
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => openUploadDrawer({ kind: 'profile-photo' })}
                  disabled={!canUseUploads}
                  aria-describedby={!canUseUploads ? 'tap-photo-upload-help' : undefined}
                >
                  {photoUrl ? 'Change photo' : 'Choose upload'}
                </button>
                {!canUseUploads ? (
                  <p id="tap-photo-upload-help" className="tap-helper-text">
                    {canSyncToCloud
                      ? 'Confirm draft ownership and review retained work before uploading assets.'
                      : 'Uploads unlock after you order a card or upgrade.'}
                  </p>
                ) : null}
              </div>
              <div className="tap-profile-fields">
                <label>
                  <span>Name</span>
                  <input value={tapDocument.profile.name} onChange={(event) => handleProfileChange('name', event.target.value, textEditGroup(event.currentTarget))} placeholder="Alex Chen" />
                </label>
                <label>
                  <span>Title</span>
                  <input value={tapDocument.profile.title} onChange={(event) => handleProfileChange('title', event.target.value, textEditGroup(event.currentTarget))} placeholder="Creative Director" />
                </label>
                <label>
                  <span>Subtitle (optional)</span>
                  <input value={tapDocument.profile.subtitle ?? ''} onChange={(event) => handleProfileChange('subtitle', event.target.value, textEditGroup(event.currentTarget))} placeholder="InkTap Studios" />
                </label>
                <label>
                  <span>Short bio (optional)</span>
                  <textarea value={tapDocument.profile.bio ?? ''} onChange={(event) => handleProfileChange('bio', event.target.value, textEditGroup(event.currentTarget))} placeholder="Share a quick intro that helps visitors take action." rows={3} />
                </label>
              </div>
            </div>
            <div className="tap-profile-url">
              <label htmlFor="tap-profile-photo-url">Profile photo URL</label>
              <div className="tap-field-with-actions">
                <input id="tap-profile-photo-url" ref={photoUrlRef} value={photoUrl}
                  onChange={(event) => { setPhotoUrlEditing(true); handleProfileChange('photoUrl', event.target.value, textEditGroup(event.currentTarget)); }}
                  onBlur={() => setPhotoUrlEditing(false)}
                  aria-describedby={showPhotoError ? 'tap-photo-error' : undefined} placeholder="https://..." />
                {tapDocument.profile.photoUrl ? (
                  <button type="button" onClick={handleClearPhoto}>Remove</button>
                ) : null}
              </div>
              {showPhotoError ? (
                <div id="tap-photo-error" className="tap-profile-photo-error" role="status">
                  <span>We couldn't load this photo. {canUseUploads ? 'Check the URL or choose another upload.' : 'Check the URL and try again.'}</span>
                  <button type="button" onClick={() => { setPhotoUrlEditing(false); setFailedPhotoUrl(null); photoUrlRef.current?.focus(); }}>Retry photo</button>
                </div>
              ) : null}
            </div>
          </section>

        <section className="tap-panel" data-editor-section="content" hidden={editorStage !== 'content'}>
          <div className="tap-actions-heading">
            <h2 ref={actionsHeadingRef} tabIndex={-1}>Your actions</h2>
            {tapDocument.actions.length > 0 ? (
              <div className="tap-actions-heading__controls">
                <button type="button" onClick={() => setExpandedActions(new Set())}>Collapse all</button>
                <button type="button" onClick={() => setExpandedActions(new Set(tapDocument.actions.map((action) => action.id)))}>Expand all</button>
              </div>
            ) : null}
          </div>
          <p className="tap-helper-text">
            {actionLimit === null ? 'Add the actions you need.' : `Your plan supports up to ${actionLimit} actions.`} Use the move buttons to reorder them.
          </p>
          <p className="sr-only" role="status">{actionAnnouncement}</p>
          {actionLimit !== null && tapDocument.actions.length > actionLimit ? (
            <p className="tap-helper-text tap-helper-text--warning" role="status">
              Your existing actions are preserved. Remove {tapDocument.actions.length - actionLimit} to save or publish under your current plan.
            </p>
          ) : null}
          {providerRegistryStatus === 'error' ? (
            <p className="tap-helper-text tap-helper-text--warning">
              Couldn’t load the latest provider list. Showing the fallback options for now.
            </p>
          ) : null}
          {!canUseUploads ? (
            <p className="tap-helper-text">{canSyncToCloud ? "Confirm draft ownership and review retained work before attaching uploads." : "Uploads unlock after you order a card or upgrade."}</p>
          ) : null}
          {tapDocument.actions.map((action, index) => {
            const providerId = typeof action.provider === 'string' ? action.provider : undefined;
            const actionUrl = action.url ?? '';
            const forced = isForceEmbedEnabled(providerId, actionUrl);

            return (
              <div key={action.id} className="tap-action-editor">
                <div className="tap-action-editor__header">
                  <button
                    type="button"
                    className="tap-action-editor__toggle"
                    ref={(element) => {
                      if (element) actionToggleRefs.current.set(action.id, element);
                      else actionToggleRefs.current.delete(action.id);
                    }}
                    aria-expanded={expandedActions.has(action.id)}
                    aria-controls={`tap-action-fields-${index}`}
                    onClick={() => setExpandedActions((previous) => {
                      const next = new Set(previous);
                      if (next.has(action.id)) next.delete(action.id);
                      else next.add(action.id);
                      return next;
                    })}
                  >
                    <span className="tap-action-editor__icon"><TapActionIcon type={action.type} /></span>
                    <span className="tap-action-editor__summary">
                      <strong id={`tap-action-label-${index}`}>{getActionLabel(action)}</strong>
                      <small>{action.url || 'Open to edit'}</small>
                    </span>
                    <span aria-hidden="true">{expandedActions.has(action.id) ? '▴' : '▾'}</span>
                  </button>
                  <div className="tap-action-editor__controls">
                    <button
                      type="button"
                      onClick={() => {
                        if (index === 1) pendingActionFocus.current = { id: action.id };
                        handleMoveAction(action.id, -1);
                        setActionAnnouncement(`${getActionLabel(action)} moved to position ${index} of ${tapDocument.actions.length}.`);
                      }}
                      disabled={index === 0}
                      aria-label="Move action up"
                      aria-describedby={`tap-action-label-${index}`}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (index === tapDocument.actions.length - 2) pendingActionFocus.current = { id: action.id };
                        handleMoveAction(action.id, 1);
                        setActionAnnouncement(`${getActionLabel(action)} moved to position ${index + 2} of ${tapDocument.actions.length}.`);
                      }}
                      disabled={index === tapDocument.actions.length - 1}
                      aria-label="Move action down"
                      aria-describedby={`tap-action-label-${index}`}
                    >
                      ↓
                    </button>
                    <button type="button" aria-describedby={`tap-action-label-${index}`} onClick={() => {
                      pendingActionFocus.current = { id: tapDocument.actions[index + 1]?.id ?? tapDocument.actions[index - 1]?.id ?? null };
                      handleRemoveAction(action.id);
                      setActionAnnouncement(`${getActionLabel(action)} removed. ${tapDocument.actions.length - 1} actions remain.`);
                    }}>
                      Remove
                    </button>
                  </div>
                </div>
                <div id={`tap-action-fields-${index}`} className="tap-action-editor__fields" hidden={!expandedActions.has(action.id)}>
                <label>
                  <span>Action type</span>
                  <select
                    value={action.type}
                    onChange={(event) => handleActionTypeChange(action.id, event.target.value as TapActionType)}
                  >
                    {ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {(() => {
                  if (action.type === 'booking' || action.type === 'service-book' || action.type === 'social') {
                    const providerOptions =
                      action.type === 'booking'
                        ? bookingProviderOptions
                        : action.type === 'service-book'
                        ? serviceProviderOptions
                        : SOCIAL_PROVIDERS;
                    const defaultProvider =
                      action.type === 'booking'
                        ? 'calendly'
                        : action.type === 'service-book'
                        ? 'acuity'
                        : 'linkedin';
                    const labelCopy =
                      action.type === 'booking'
                        ? 'Meeting provider'
                        : action.type === 'service-book'
                        ? 'Service provider'
                        : 'Network';
                    if (action.type === 'booking' || action.type === 'service-book') {
                      return (
                        <>
                          <fieldset className="tap-booking-type-toggle">
                            <legend>Booking type</legend>
                            <div className="tap-booking-type-toggle__options">
                              {BOOKING_TYPE_PRESETS.map((preset) => (
                                <button
                                  key={preset.value}
                                  type="button"
                                  className={`tap-booking-type-toggle__option${
                                    preset.value === action.type ? ' is-active' : ''
                                  }`}
                                  onClick={() => {
                                    if (preset.value === action.type) {
                                      return;
                                    }
                                    handleActionTypeChange(action.id, preset.value);
                                  }}
                                >
                                  <span>{preset.label}</span>
                                  <small>{preset.description}</small>
                                </button>
                              ))}
                            </div>
                          </fieldset>
                          <label>
                            <span>{labelCopy}</span>
                            <select
                              value={(action.provider as string | undefined) ?? defaultProvider}
                              onChange={(event) => {
                                if (action.type === 'booking') {
                                  handleActionChange(action.id, {
                                    provider: event.target.value as TapBookingProvider
                                  });
                                  return;
                                }
                                handleActionChange(action.id, {
                                  provider: event.target.value as TapServiceProvider
                                });
                              }}
                            >
                              {providerOptions.map((provider) => (
                                <option key={provider.value} value={provider.value}>
                                  {provider.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </>
                      );
                    }
                    if (action.type === 'social') {
                      return (
                        <label>
                          <span>{labelCopy}</span>
                          <select
                            value={(action.provider as string | undefined) ?? defaultProvider}
                            onChange={(event) => {
                              handleActionChange(action.id, {
                                provider: event.target.value as TapSocialProvider
                              });
                            }}
                          >
                            {providerOptions.map((provider) => (
                              <option key={provider.value} value={provider.value}>
                                {provider.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      );
                    }
                    return (
                      <label>
                        <span>{labelCopy}</span>
                        <select
                          value={(action.provider as string | undefined) ?? defaultProvider}
                          onChange={(event) => {
                            if (action.type === 'booking') {
                              handleActionChange(action.id, {
                                provider: event.target.value as TapBookingProvider
                              });
                              return;
                            }
                            handleActionChange(action.id, {
                              provider: event.target.value as TapServiceProvider
                            });
                          }}
                        >
                          {providerOptions.map((provider) => (
                            <option key={provider.value} value={provider.value}>
                              {provider.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  }
                  return null;
                })()}
                <label>
                  <span>
                    {action.type === 'download-card'
                      ? 'vCard or link URL'
                      : action.type === 'booking' || action.type === 'service-book'
                      ? 'Scheduling URL'
                      : 'Destination URL'}
                  </span>
                  <div className="tap-field-with-actions">
                    <input
                      value={action.url ?? ''}
                      onChange={(event) => handleActionChange(action.id, { url: event.target.value }, textEditGroup(event.currentTarget))}
                      placeholder="https://..."
                    />
                    <button
                      type="button"
                      onClick={() =>
                        openUploadDrawer({ kind: 'action', actionId: action.id, actionType: action.type })
                      }
                      disabled={!canUseUploads}
                    >
                      Choose upload
                    </button>
                  </div>
                  {!canUseUploads ? (
                    <p className="tap-helper-text">
                      {canSyncToCloud
                        ? 'Confirm draft ownership and review retained work before uploading assets.'
                        : 'Uploads unlock after you order a card or upgrade.'}
                    </p>
                  ) : null}
                </label>
                {action.type === 'booking' || action.type === 'service-book' ? (
                  <FrameabilityNotice
                    providerId={(action.provider as string | undefined) ?? undefined}
                    url={action.url ?? ''}
                    providerMap={providerMap}
                    isForced={forced}
                    onForceEmbedChange={
                      providerId && actionUrl
                        ? (force) => handleForceEmbedChange(providerId, actionUrl, force)
                        : undefined
                    }
                  />
                ) : null}
                {LABEL_EDITABLE_TYPES.includes(action.type) ? (
                  <label>
                    <span>Button label</span>
                    <input
                      value={action.label ?? ''}
                      onChange={(event) => handleActionChange(action.id, { label: event.target.value }, textEditGroup(event.currentTarget))}
                      placeholder={getActionLabel({ ...action, label: undefined })}
                    />
                  </label>
                ) : null}
                </div>
              </div>
            );
          })}
            {hasAvailableActions ? (
              <div className="tap-add-action">
                <div className="tap-add-action__intro">
                  <span className="tap-add-action__icon" aria-hidden="true">+</span>
                  <div><strong>Add an action</strong><p>Choose how people can connect with you.</p></div>
                </div>
                <label>
                  <span>Action type</span>
                  <select value={nextActionType} onChange={(event) => setNextActionType(event.target.value as TapActionType)}>
                    {ACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="button" onClick={handleAddAction}>
                  Add action
                </button>
              </div>
            ) : (
              <p className="tap-helper-text">You have reached the action limit for now.</p>
            )}
            <button type="button" className="tap-builder__continue" onClick={() => goToStage('appearance')}>Continue to appearance</button>
          </section>
          <section className="tap-panel" data-editor-section="appearance" hidden={editorStage !== 'appearance'}>
            <h2>Appearance</h2>
            <p className="tap-helper-text">Choose how your Tap Page appears to visitors.</p>
            <fieldset className="tap-fieldset">
              <legend><span ref={appearanceRef} tabIndex={-1}>Theme</span></legend>
              {(['auto', 'light', 'dark'] as TapTheme[]).map((themeOption) => (
                <label
                  key={themeOption}
                  className="tap-radio"
                  data-active={tapDocument.theme === themeOption}
                >
                  <input
                    type="radio"
                    name="tap-theme"
                    value={themeOption}
                    checked={tapDocument.theme === themeOption}
                    onChange={() => handleThemeChange(themeOption)}
                  />
                  <span>{themeOption === 'auto' ? 'Auto (match visitor)' : themeOption}</span>
                </label>
              ))}
            </fieldset>
            <button type="button" className="tap-builder__continue" onClick={() => goToStage('preview')}>Continue to preview</button>
          </section>
        </aside>

        <section className="tap-builder__preview" aria-labelledby="tap-live-preview-title" data-editor-section="preview" tabIndex={0}>
          <div className="tap-preview__header">
            <div className="tap-preview__heading">
              <h2 id="tap-live-preview-title" ref={previewHeadingRef} tabIndex={-1}>Live preview</h2>
              <p className="tap-preview__note">Updates as you edit. Device widths are approximate and fit the available space; use Preview to open the page in your browser.</p>
            </div>
            <div className="tap-device-toggle" role="group" aria-label="Preview device">
              {(['mobile', 'tablet', 'desktop'] as BuilderDevice[]).map((device) => (
                <button
                  key={device}
                  type="button"
                  data-active={activeDevice === device}
                  aria-pressed={activeDevice === device}
                  onClick={() => handleDeviceChange(device)}
                >
                  {device}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="tap-builder__jump tap-builder__jump--back" onClick={() => goToStage('content')}>
            Back to editing
          </button>
          <div
            className="tap-preview__canvas"
            data-device={activeDevice}
            data-theme={tapDocument.theme}
            style={{ width: DEVICE_WIDTH[activeDevice] }}
          >
            <div
              className="tap-preview__content"
              key={previewPhotoKey}
              ref={previewContentRef}
              dangerouslySetInnerHTML={{ __html: previewMarkup }}
            />
          </div>
          <details className="tap-viewport-preview" onToggle={(event) => setShowViewportPreview(event.currentTarget.open)}>
            <summary>View at actual device width</summary>
            <p>Read-only first screen at {VIEWPORT_WIDTH[activeDevice]} px. Scroll sideways to see the whole width; use the live preview above to try actions.</p>
            {showViewportPreview && (
              <div className="tap-viewport-preview__scroll" role="region" aria-label="Device width preview, scroll horizontally" tabIndex={0}>
                <iframe
                  key={previewPhotoKey}
                  title="Read-only device width preview"
                  aria-hidden="true"
                  tabIndex={-1}
                  sandbox=""
                  style={{ width: VIEWPORT_WIDTH[activeDevice] }}
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"><style>${previewCss}\n${viewportPhotoCss}</style></head><body>${previewHtml}</body></html>`}
                />
              </div>
            )}
          </details>
          {editorStage === 'preview' ? <button type="button" className="tap-builder__continue" onClick={() => goToStage('publish')}>Continue to publish settings</button> : null}
        </section>

        <aside className="tap-builder__inspector" aria-label="Landing link and validation" hidden={editorStage !== 'publish'}>
          <section className="tap-panel">
            <h2 ref={publishHeadingRef} tabIndex={-1}>Landing link</h2>
            <label>
              <span>Slug</span>
              <input value={meta.slug} onChange={(event) => handleMetaChange('slug', event.target.value, textEditGroup(event.currentTarget))} />
            </label>
            <label>
              <span>Page title (SEO)</span>
              <input
                value={meta.title}
                onChange={(event) => handleMetaChange('title', event.target.value, textEditGroup(event.currentTarget))}
                placeholder="Tap landing page"
              />
            </label>
            <label>
              <span>Page description</span>
              <textarea
                value={meta.description}
                onChange={(event) => handleMetaChange('description', event.target.value, textEditGroup(event.currentTarget))}
                placeholder="Give visitors a short description for search results."
                rows={3}
              />
            </label>
            <div className="tap-helper-card">
              <p>Sharing uses your published page. Draft changes appear after you publish.</p>
              <Link to="/tap">Share published page</Link>
            </div>
          </section>
          <section className="tap-panel">
            <h2 ref={validationHeadingRef} tabIndex={-1}>Validation</h2>
            {validationErrors.length === 0 ? (
              <p className="tap-helper-text">No validation issues found.</p>
            ) : (
              <ul className="tap-error-list">
                {validationErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
            <button type="button" onClick={performValidation}>
              Run validation
            </button>
            {validationErrors.length > 0 ? <button type="button" onClick={() => goToStage('content')}>Return to content to make changes</button> : null}
          </section>
        </aside>
      </div>

      <UploadDrawer
        open={isUploadDrawerOpen}
        onClose={handleCloseUploadDrawer}
        onInsert={handleInsertUpload}
        apiBaseUrl={endpoint}
        slug={currentSlug}
      />
    </div>
  );
};

const TapBuilder = (props: TapBuilderProps) => {
  const { session } = useSession();
  const scopeKey = JSON.stringify([session?.subjectId ?? null, session?.workspaceId ?? null,
    session?.email ?? null, session?.active ?? false, props.initialSlug ?? null, props.apiBaseUrl ?? API_BASE_URL]);
  return <TapBuilderEditor key={scopeKey} {...props} />;
};
export { TapBuilder };

type FrameabilityNoticeProps = {
  providerId?: string | null;
  url?: string | null;
  providerMap: Map<string, ProviderRegistryEntry>;
  isForced?: boolean;
  onForceEmbedChange?: (force: boolean) => void;
};

function FrameabilityNotice({
  providerId,
  url,
  providerMap,
  isForced = false,
  onForceEmbedChange
}: FrameabilityNoticeProps) {
  const provider = providerId ? providerMap.get(providerId) : null;
  const trimmedUrl = url?.trim() ?? '';
  const supportedUrl = provider?.supportsEmbed ? matchesProviderUrl(provider, trimmedUrl) : false;
  const [state, setState] = useState<'idle' | 'loading' | 'ok' | 'blocked' | 'unknown' | 'error'>('idle');

  useEffect(() => {
    if (!provider || !trimmedUrl || (provider.supportsEmbed && !supportedUrl)) {
      setState('idle');
      return;
    }
    if (!provider.supportsEmbed) {
      setState('blocked');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      if (cancelled) {
        return;
      }
      setState('loading');
      fetchFrameabilityStatus(provider.id, trimmedUrl, controller.signal)
        .then((result) => {
          if (cancelled) {
            return;
          }
          if (result.status === 'OK') {
            setState('ok');
          } else if (result.status === 'BLOCKED') {
            setState('blocked');
          } else {
            setState('unknown');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setState('error');
          }
        });
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
      controller.abort();
    };
  }, [provider, trimmedUrl, supportedUrl]);

  if (!provider || !trimmedUrl || (provider.supportsEmbed && !supportedUrl)) {
    return null;
  }

  let message: string | null = null;
  let className = 'tap-helper-text';
  const canForceOverride = Boolean(onForceEmbedChange) && provider.supportsEmbed;
  let helperAction: JSX.Element | null = null;

  switch (state) {
    case 'loading':
      message = 'Checking embed status…';
      break;
    case 'ok':
      message = 'Embed ready — visitors will stay on the page.';
      className += ' tap-helper-text--success';
      break;
    case 'blocked':
      className += ' tap-helper-text--warning';
      message = provider.supportsEmbed
        ? 'Provider blocked embeds (CSP/X-Frame). This button will open in a new tab.'
        : 'This provider always opens in a new tab.';
      break;
    case 'unknown':
      className += ' tap-helper-text--warning';
      message = 'Unable to confirm embed support. We’ll open externally.';
      break;
    case 'error':
      className += ' tap-helper-text--warning';
      message = 'Failed to check embed support. Falling back to link-out.';
      break;
    case 'idle':
    default:
      return null;
  }

  if (isForced && canForceOverride) {
    className = 'tap-helper-text tap-helper-text--warning';
    if (state === 'blocked') {
      message = 'Embed forced for testing — provider previously blocked framing. Verify before launch.';
    } else if (state === 'unknown') {
      message = 'Embed status unknown — forcing embed for testing. Validate the provider experience.';
    } else if (state === 'error') {
      message = 'Force-enabled embed after a frameability check error. Confirm behaviour on real devices.';
    } else {
      message = 'Embed forced for testing. Disable once provider checks pass.';
    }
    helperAction = (
      <button
        type="button"
        className="tap-helper-action-button"
        data-variant="danger"
        onClick={() => onForceEmbedChange?.(false)}
      >
        Use safe link-out
      </button>
    );
  } else if (canForceOverride && (state === 'blocked' || state === 'unknown' || state === 'error')) {
    helperAction = (
      <button
        type="button"
        className="tap-helper-action-button"
        onClick={() => onForceEmbedChange?.(true)}
      >
        Force embed for testing
      </button>
    );
  }

  if (!message && !helperAction) {
    return null;
  }

  return (
    <div className="tap-helper-block">
      {message ? <p className={className}>{message}</p> : null}
      {helperAction ? <div className="tap-helper-actions">{helperAction}</div> : null}
    </div>
  );
}

export { FrameabilityNotice };
